import Highcharts from 'highcharts';
import { getSeriesStackOrder } from './utils/getSeriesStackOrder';
import { chartRenderEvent } from './utils/chartRenderEvent';
import { formatCurrency } from '../../../../../../CommonUtils/formatNumber';
import {
  PlanSeriesName,
  RetirementAssetsChartDataDTO,
  RetirementAssetsChartDTO,
  SM_BREAK,
} from './RetirementChart.meta';
import { useTheme } from '../../../../../../theme/theme';

let globalData: RetirementAssetsChartDataDTO;

interface Params {
  isCurrentPlan: boolean;
  isPrint?: boolean;
  data: RetirementAssetsChartDTO;
}

export const useRetirementChart = ({ data, isCurrentPlan, isPrint }: Params) => {
  const theme = useTheme();

  const { Data, IsEmpty } = data;

  /**
   * Due to a Highcharts bug (https://github.com/highcharts/highcharts-react/issues/112)
   * a global reference to Data is needed to have the correct values inside the render event.
   */
  globalData = Data;

  const {
    PrincipalLifeExpectancy,
    SpouseLifeExpectancy,
    PrincipalLifeExpectancyIndex,
    SpouseLifeExpectancyIndex,
    Categories,
    HasSpouse,
    Series,
  } = Data;

  const currentPlanSeries = Series.Current;
  const recommendedPlanSeries = Series.Recommended;

  const seriesStackOrder = getSeriesStackOrder(
    currentPlanSeries,
    recommendedPlanSeries,
    isCurrentPlan,
  );

  const getTooltipAges = (year: number) => {
    try {
      const principalAge =
        PrincipalLifeExpectancy + year - +Categories[PrincipalLifeExpectancyIndex];

      const principalAgeLabel = principalAge > PrincipalLifeExpectancy ? '-' : `${principalAge}`;

      if (!HasSpouse) {
        return `Age ${principalAgeLabel}:`;
      }

      const spouseAge = SpouseLifeExpectancy + year - +Categories[SpouseLifeExpectancyIndex];

      const spouseAgeLabel = spouseAge > SpouseLifeExpectancy ? '-' : `${spouseAge}`;

      return `Age ${principalAgeLabel}/${spouseAgeLabel}:`;
    } catch (err) {
      if (err instanceof Error) {
        console.error(err.message);
      }

      return '';
    }
  };

  const chartSeries = seriesStackOrder.map((seriesName, seriesIndex) => ({
    name: seriesName,
    color: '',
    data: Series[seriesName].map((series, index) => ({
      x: parseInt(Categories[index], 10),
      y: series,
    })),
    zIndex: seriesIndex === 0 ? 2 : 1,
  }));

  const chartConfig: Highcharts.Options = {
    credits: {
      enabled: false,
    },
    title: undefined,
    chart: {
      height: 430,
      spacingRight: 20,
      spacingBottom: 100,
      spacingTop: 50,
      style: isPrint ? { fontSize: '16px' } : { fontFamily: 'Inter', fontSize: '16px' },
      events: {
        render(evt) {
          const chart = (evt.target as unknown) as Highcharts.Chart;

          chartRenderEvent(chart, globalData, theme, 'overview');
        },
      },
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.8,
      },
      series: {
        marker: { enabled: false, symbol: 'circle' },
        events: {
          legendItemClick: event => {
            event.preventDefault();
          },
        },
      },
    },
    colors: seriesStackOrder.map(seriesName =>
      seriesName === PlanSeriesName.Current
        ? theme.colors.primary['300']
        : theme.colors.primary['100'],
    ),
    legend: {
      enabled: false,
    },
    tooltip: {
      borderRadius: 10,
      shadow: false,
      useHTML: true,
      backgroundColor: theme.colors.neutral['700'],
      borderColor: theme.colors.neutral['700'],
      shape: 'square',
      style: {
        color: theme.colors.neutral['100'],
      },
      formatter() {
        return `
            <div style='display: flex; align-items: flex-start;'>
              <div style='width: 8px; height: 8px; border-radius: 4px; margin-top: 5px; margin-right: 4px; background: ${
                this.series.name === PlanSeriesName.Current
                  ? `${theme.colors.primary['300']}`
                  : `${theme.colors.primary['100']}`
              }'></div>
              
              <div style='font-size: 12px; color: ${theme.colors.neutral['100']};'>
                <div style='margin-bottom: 4px;'>
                  <span>${
                    this.series.name === PlanSeriesName.Current ? 'Current Plan:' : 'Proposed Plan:'
                  }</span>
                  <span style='font-weight: 600; margin-left: 4px;'>${this.x}</span>
                </div>
                <div>
                  <span>${getTooltipAges(Number(this.x))}</span>
                  <span style='font-weight: 600; margin-left: 4px;'>${formatCurrency(
                    Number(this.y),
                  )}</span>
                </div>
              </div>
            </div>
          `;
      },
    },
    xAxis: {
      tickWidth: 0,
      allowDecimals: false,
      labels: {
        style: {
          color: theme.colors.neutral['500'],
        },
      },
    },
    yAxis: {
      title: {
        text: null,
      },
      max: IsEmpty ? 50 : null,
      labels: {
        enabled: true,
        formatter() {
          return formatCurrency(Number(this.value), {
            average: true,
            mantissa: 2,
            optionalMantissa: true,
            trimMantissa: true,
          }).toUpperCase();
        },
        style: {
          color: theme.colors.neutral['500'],
        },
      },
      gridLineDashStyle: 'Solid',
      gridLineColor: theme.colors.neutral['200'],
    },
    series: chartSeries.map(_series => ({
      ..._series,
      type: 'areaspline',
      lineWidth: 2,
      lineColor:
        _series.name === 'Recommended' ? theme.colors.primary['500'] : theme.colors.primary['300'],
      marker: {
        fillColor:
          _series.name === 'Recommended'
            ? theme.colors.primary['500']
            : theme.colors.primary['300'],
      },
    })),
    responsive: {
      rules: [
        {
          condition: { maxWidth: SM_BREAK },
          chartOptions: {
            annotations: [],
            legend: {
              margin: 30,
            },
            yAxis: {
              labels: {
                x: 8,
              },
            },
          },
        },
      ],
    },
  };

  return {
    chartConfig,
  };
};
